import React, { useEffect, useRef, useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import styles from './CheckoutReturnPage.module.css';

/**
 * CheckoutReturnPage — the Stripe Checkout success return (design Screen 2).
 *
 * Bridges the webhook-vs-redirect race: after the user pays on Stripe and is
 * redirected here, we poll `GET /api/billing/subscription` every 1.5s for up to
 * 10s while showing "Activating your plan…". Once the subscription reflects the
 * paid plan we show the success state. If we hit the 10s ceiling without the
 * plan activating, we drop to a soft "Still processing — we've emailed your
 * receipt" message (intentionally NOT an error — the webhook usually lands
 * shortly after).
 *
 * Mock mode (`?mock=1`, set by `useStartCheckout` mock redirect): show the
 * success state immediately so Playwright can assert the happy path without a
 * real Stripe round-trip and without waiting on the backend mirror.
 *
 * @param {object} props
 * @param {() => void} [props.onContinue] - Navigate to the post-checkout home
 *   (e.g. the buyer hub) when the user clicks the primary CTA.
 * @param {() => void} [props.onViewBilling] - Navigate to billing settings.
 */

const POLL_INTERVAL_MS = 1500;
const POLL_CEILING_MS = 10000;
const ACTIVATED_STATUSES = new Set(['active', 'trialing']);

function isMockReturn() {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('mock') === '1';
  } catch {
    return false;
  }
}

/** A subscription reflects a paid plan once it is no longer Explorer
 *  and carries an activating/active status. */
function isPaidPlanActivated(subscription) {
  if (!subscription) return false;
  if (subscription.plan_code === 'explorer') return false;
  if (!subscription.plan_code) return false;
  return subscription.status == null
    ? false
    : ACTIVATED_STATUSES.has(subscription.status);
}

export default function CheckoutReturnPage({ onContinue, onViewBilling }) {
  const mock = isMockReturn();
  const { subscription, refetch } = useSubscription();
  // 'polling' | 'success' | 'timeout'
  const [phase, setPhase] = useState(mock ? 'success' : 'polling');
  const startRef = useRef(Date.now());
  const pollRef = useRef(null);
  const headingRef = useRef(null);

  // Poll the subscription mirror until the plan activates or we hit the ceiling.
  useEffect(() => {
    if (mock) return undefined;

    const tick = async () => {
      const data = await refetch();
      if (isPaidPlanActivated(data)) {
        setPhase('success');
        return;
      }
      if (Date.now() - startRef.current >= POLL_CEILING_MS) {
        setPhase('timeout');
        return;
      }
      pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // refetch is stable (useCallback); run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mock]);

  // If a passive refetch (outside the poll loop) lands the plan, reflect it.
  useEffect(() => {
    if (phase === 'polling' && isPaidPlanActivated(subscription)) {
      setPhase('success');
    }
  }, [subscription, phase]);

  // Keep the document title + focus in sync for screen readers (design Screen 2).
  useEffect(() => {
    const titles = {
      polling: 'Activating your plan — cutthe6.com',
      success: 'Plan active — cutthe6.com',
      timeout: 'Still processing — cutthe6.com',
    };
    document.title = titles[phase] || document.title;
    if (phase !== 'polling' && headingRef.current) {
      headingRef.current.focus();
    }
  }, [phase]);

  const heading =
    phase === 'success'
      ? 'Your plan is active'
      : phase === 'timeout'
        ? 'Still processing'
        : 'Activating your plan…';

  return (
    <div className={styles.page}>
      <div
        className={styles.card}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-busy={phase === 'polling'}
        data-testid="checkout-return"
      >
        {phase === 'success' && (
          <span className={styles.successIcon} aria-hidden="true">✓</span>
        )}

        <h1 className={styles.title} ref={headingRef} tabIndex={-1}>
          {heading}
        </h1>

        {phase === 'polling' && (
          <>
            <p className={styles.message}>
              We're confirming your payment with Stripe. This usually takes a few
              seconds.
            </p>
            <div className={styles.dots} aria-hidden="true">
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          </>
        )}

        {phase === 'success' && (
          <div data-testid="checkout-success">
            <p className={styles.message}>
              Welcome aboard. Your payment is confirmed.
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primary}
                onClick={() => onContinue && onContinue()}
                data-testid="checkout-success-continue"
              >
                Go to dashboard
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => (onViewBilling ? onViewBilling() : onContinue && onContinue())}
              >
                View your billing
              </button>
            </div>
          </div>
        )}

        {phase === 'timeout' && (
          <>
            <p className={styles.message}>
              Your plan will activate within a few minutes — we've emailed your
              receipt. You can keep going and it'll catch up automatically.
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primary}
                onClick={() => onContinue && onContinue()}
              >
                Go to dashboard
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => refetch()}
              >
                Refresh
              </button>
            </div>
          </>
        )}

        <span className={styles.srOnly}>Checkout status: {phase}.</span>
      </div>
    </div>
  );
}
